import { env } from '@/config/env';
import { isTelegramMiniApp } from './telegramWebApp';
import type { LatLon } from '@/modules/geolocation';
import { bboxAround, NEARBY_RADIUS_M } from '@/modules/geolocation';


/** localStorage flag mirroring whether the user opted into background push. */
const ENABLED_KEY = 'pushka:pushEnabled';

type Bbox = [number, number, number, number];

/**
 * Web Push works only for an installed standalone PWA with a service worker — never
 * inside the Telegram Mini App webview (no PushManager there).
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    !isTelegramMiniApp()
  );
}

export function isPushEnabled(): boolean {
  return localStorage.getItem(ENABLED_KEY) === '1';
}

/** Resolve the VAPID public key from env, falling back to the backend endpoint. */
async function getVapidPublicKey(): Promise<string> {
  if (env.vapidPublicKey) return env.vapidPublicKey;
  const res = await fetch(`${env.apiUrl}/push/vapid-public-key`);
  if (!res.ok) throw new Error('Не удалось получить ключ push-сервера');
  const { publicKey } = (await res.json()) as { publicKey: string };
  if (!publicKey) throw new Error('Push-сервер не сконфигурирован (нет VAPID-ключа)');
  return publicKey;
}

/** Convert a base64url VAPID key to the Uint8Array applicationServerKey expects. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function bboxFor(center: LatLon): Bbox {
  return bboxAround(center, NEARBY_RADIUS_M) as Bbox;
}

/** True when an existing subscription was minted with the given VAPID key. */
function keyMatchesSubscription(sub: PushSubscription, key: Uint8Array<ArrayBuffer>): boolean {
  const existing = sub.options.applicationServerKey;
  if (!existing) return false;
  const bytes = new Uint8Array(existing as ArrayBuffer);
  if (bytes.length !== key.length) return false;
  for (let i = 0; i < bytes.length; i++) if (bytes[i] !== key[i]) return false;
  return true;
}

async function postJson(path: string, body: unknown): Promise<void> {
  const res = await fetch(`${env.apiUrl}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Push API ${path} failed: ${res.status}`);
}

/**
 * Request permission, subscribe via PushManager and register the subscription + the
 * user's region of interest with the backend. Throws on denial / unsupported.
 */
export async function enablePush(center: LatLon): Promise<void> {
  if (!isPushSupported()) throw new Error('Push-уведомления не поддерживаются');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Доступ к уведомлениям запрещён');

  const registration = await navigator.serviceWorker.ready;
  const applicationServerKey = urlBase64ToUint8Array(await getVapidPublicKey());

  let subscription = await registration.pushManager.getSubscription();
  // Drop a subscription minted under a different VAPID key (e.g. the server rotated
  // its keypair): the server would sign pushes the browser silently rejects.
  if (subscription && !keyMatchesSubscription(subscription, applicationServerKey)) {
    await subscription.unsubscribe().catch(() => {});
    subscription = null;
  }

  let freshlySubscribed = false;
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    freshlySubscribed = true;
  }

  const json = subscription.toJSON();
  try {
    await postJson('/push/subscribe', {
      endpoint: json.endpoint,
      keys: json.keys,
      region: bboxFor(center),
    });
  } catch (err) {
    // Roll back the browser subscription we just created so it doesn't linger unknown
    // to the server (a phantom subscription the backend can never address or prune).
    if (freshlySubscribed) await subscription.unsubscribe().catch(() => {});
    throw err;
  }

  localStorage.setItem(ENABLED_KEY, '1');
}

/** Move the region of interest with the user (called when they meaningfully relocate). */
export async function updatePushRegion(center: LatLon): Promise<void> {
  if (!isPushEnabled() || !isPushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  await postJson('/push/region', {
    endpoint: subscription.endpoint,
    region: bboxFor(center),
  });
}

export async function disablePush(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const { endpoint } = subscription;
        await subscription.unsubscribe().catch(() => {});
        await postJson('/push/unsubscribe', { endpoint }).catch(() => {});
      }
    }
  } finally {
    // Always reflect "off" in the UI flag, even if teardown above failed.
    localStorage.removeItem(ENABLED_KEY);
  }
}
